import React, { useState, useEffect, useRef } from 'react';
import { auth, api } from './auth';
import Login from './Login';
import MfaEnrollmentGate from './MfaEnrollmentGate';
import History from './History';
import Dashboard from './Dashboard';
import AdminCompanies from './AdminCompanies';
import AdminUsers from './AdminUsers';
import AdminRequirements from './AdminRequirements';
import AdminBilling from './AdminBilling';
import AdminPayments from './AdminPayments';
import AdminSettings from './AdminSettings';
import AccountSecurity from './AccountSecurity';
import AccountAPIKeys from './AccountAPIKeys';
import AdminDataSubjectRequests from './AdminDataSubjectRequests';

const API = process.env.REACT_APP_API_URL || '';

function UploadZone({ label, accept, icon, onFile, loading, activeFile }) {
  const [dragging, setDragging] = useState(false);
  const ref = useRef();
  return (
    <div onClick={() => !loading && ref.current.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
      style={{ border: `1.5px dashed ${dragging ? '#2563EB' : '#D1D5DB'}`, borderRadius: 12, padding: '28px 20px', textAlign: 'center', cursor: loading ? 'not-allowed' : 'pointer', background: dragging ? '#EFF6FF' : '#fff', flex: 1, minWidth: 220 }}>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }} onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); }} />
      <div style={{ fontSize: 28, marginBottom: 8 }}>{icon}</div>
      <p style={{ fontWeight: 500, color: '#111827', marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 12, color: '#9CA3AF' }}>{accept}</p>
      {activeFile && <p style={{ fontSize: 12, color: '#2563EB', marginTop: 8, fontWeight: 500 }}>{activeFile.name}</p>}
    </div>
  );
}

function Badge({ verdict, size = 'md' }) {
  const m = { COMPLIANT: ['#F0FDF4','#16A34A','#BBF7D0'], 'NON-COMPLIANT': ['#FEF2F2','#DC2626','#FECACA'], INCOMPLETE: ['#FFFBEB','#D97706','#FDE68A'], ERROR: ['#F3F4F6','#6B7280','#E5E7EB'] };
  const [bg, color, border] = m[verdict] || m.ERROR;
  const s = size === 'sm' ? { fontSize: 11, padding: '2px 8px' } : { fontSize: 13, padding: '4px 14px' };
  return <span style={{ background: bg, color, border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, ...s }}>{verdict}</span>;
}

function FieldCard({ label, fkey, data }) {
  const present = data?.present;
  return <div style={{ border: `1px solid ${present ? '#BBF7D0' : '#FECACA'}`, background: present ? '#F0FDF4' : '#FEF2F2', borderRadius: 10, padding: '12px 14px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
      <span style={{ fontSize: 12, color: present ? '#16A34A' : '#DC2626' }}>{present ? '✓' : '✗'}</span>
      <span style={{ fontSize: 11, fontWeight: 500, color: present ? '#15803D' : '#B91C1C' }}>{label}</span>
      {data?.confidence && <span style={{ marginLeft: 'auto', fontSize: 10, padding: '1px 6px', borderRadius: 4, background: data.confidence === 'high' ? '#DCFCE7' : data.confidence === 'medium' ? '#FEF9C3' : '#FEE2E2', color: data.confidence === 'high' ? '#166534' : data.confidence === 'medium' ? '#854D0E' : '#991B1B' }}>{data.confidence}</span>}
    </div>
    <p style={{ fontSize: 13, margin: 0, color: '#111827', wordBreak: 'break-all', fontFamily: (fkey === 'account_number' || fkey === 'id_number') ? 'monospace' : 'inherit' }}>{data?.value || '-'}</p>
  </div>;
}

function CheckRow({ label, data }) {
  const present = data?.present;
  return <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0', borderBottom: '0.5px solid #F3F4F6' }}>
    <span style={{ fontSize: 13, color: present ? '#16A34A' : '#DC2626', fontWeight: 700, minWidth: 16, marginTop: 1 }}>{present ? '✓' : '✗'}</span>
    <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>{label}</span>
    {data?.note && <span style={{ fontSize: 11, color: '#9CA3AF', maxWidth: 220, textAlign: 'right' }}>{data.note}</span>}
  </div>;
}

function ScoreRing({ score, size = 72 }) {
  const r = (size / 72) * 28, c = 2 * Math.PI * r, fill = (score / 100) * c;
  const color = score >= 80 ? '#16A34A' : score >= 50 ? '#D97706' : '#DC2626';
  const center = size / 2;
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
    <circle cx={center} cy={center} r={r} fill="none" stroke="#E5E7EB" strokeWidth="6" />
    <circle cx={center} cy={center} r={r} fill="none" stroke={color} strokeWidth="6" strokeDasharray={`${fill} ${c}`} strokeLinecap="round" transform={`rotate(-90 ${center} ${center})`} />
    <text x={center} y={center + 5} textAnchor="middle" fontSize={size / 5} fontWeight="600" fill={color}>{score}</text>
  </svg>;
}

function Spinner() {
  return <svg width="16" height="16" viewBox="0 0 16 16" style={{ animation: 'spin 0.8s linear infinite' }}>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <circle cx="8" cy="8" r="6" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" />
    <path d="M8 2a6 6 0 0 1 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>;
}

/**
 * ErrorBanner - renders a structured backend error in a friendly, actionable banner.
 *
 * Accepts an `error` object that may have any of these fields:
 *   message    - short human-readable description (always present)
 *   action     - italic line below the message suggesting what to do
 *   action_url - optional URL to render as a button after the action
 *   provider   - 'openai' | 'anthropic' | 'platform' | null (used as a small badge)
 *   error      - internal error code (kept hidden from the user, useful for debugging)
 */
function ErrorBanner({ error, onDismiss }) {
  if (!error) return null;
  const { message, action, action_url, provider } = error;

  const providerBadgeColours = {
    openai: { bg: '#F0F9FF', fg: '#075985' },
    anthropic: { bg: '#FAF5FF', fg: '#6B21A8' },
    platform: { bg: '#F3F4F6', fg: '#374151' },
  };
  const badge = provider && providerBadgeColours[provider];

  return (
    <div style={{
      background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10,
      padding: '14px 16px', marginBottom: 16,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#991B1B' }}>{message}</span>
            {badge && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
                background: badge.bg, color: badge.fg, textTransform: 'uppercase', letterSpacing: 0.5,
              }}>{provider}</span>
            )}
          </div>
          {action && (
            <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#7F1D1D', lineHeight: 1.5 }}>
              {action}
            </p>
          )}
          {action_url && (
            <a href={action_url} target="_blank" rel="noreferrer"
              style={{
                display: 'inline-block', marginTop: 10,
                padding: '6px 14px', borderRadius: 6,
                background: '#fff', border: '1px solid #FCA5A5',
                color: '#991B1B', fontSize: 12, fontWeight: 600,
                textDecoration: 'none',
              }}>
              Open provider page →
            </a>
          )}
        </div>
        {onDismiss && (
          <button onClick={onDismiss} style={{
            background: 'transparent', border: 'none', color: '#991B1B',
            fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 4,
          }} title="Dismiss">×</button>
        )}
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(auth.getUser());
  const [view, setView] = useState('dashboard');
  const [voiceFile, setVoiceFile] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [batch, setBatch] = useState(null);  // batch result from API
  const [selectedIdx, setSelectedIdx] = useState(0);  // which mandate is detail-shown
  const [error, setError] = useState(null);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [tab, setTab] = useState('fields');
  const [requirements, setRequirements] = useState([]);
  const [selectedReqId, setSelectedReqId] = useState('');
  const [voiceLang, setVoiceLang] = useState('en');
  const [platform, setPlatform] = useState({ system_name: 'Mandate Verifier', primary_colour: '#111827', has_logo: false });
  const [acknowledgedIds, setAcknowledgedIds] = useState({});

  useEffect(() => {
    api.get('/platform-settings').then(r => { if (r.data && typeof r.data === 'object' && r.data.system_name) setPlatform(r.data); }).catch(() => {});
    if (auth.getToken()) {
      api.get('/auth/me').then(r => setUser(r.data)).catch(() => { auth.logout(); setUser(null); });
    }
  }, []);

  // Self-serve subscriptions: if the trial has ended, prompt for a card up front.
  // The verification 402 is a backstop. Companies with no subscription return 404.
  useEffect(() => {
    if (!user) return;
    api.get('/billing/subscription').then(r => {
      const s = r.data;
      if (!s) return;
      const ended = s.status === 'expired' || s.status === 'past_due' ||
        (s.status === 'trialing' && s.trial_ends_at && new Date(s.trial_ends_at) < new Date());
      if (ended) setPaymentRequired(true);
    }).catch(() => {});
  }, [user]);

  const reloadRequirements = () => {
    if (user) api.get('/my-requirements').then(r => setRequirements(r.data)).catch(() => {});
  };
  useEffect(reloadRequirements, [user]);

  // Reset to dashboard whenever a different user logs in
  useEffect(() => {
    if (user) {
      setView('dashboard');
      setBatch(null);
      setVoiceFile(null);
      setPdfFile(null);
      setError(null);
      setSelectedIdx(0);
    }
  }, [user?.id]);

  if (typeof window !== 'undefined' && window.location.pathname === '/verify-email') {
    return <VerifyEmail />;
  }
  if (!user) return <Login onAuth={setUser} />;
  if (user.company_requires_mfa && !user.mfa_enabled) {
    return <MfaEnrollmentGate primaryColour={platform.primary_colour} onDone={setUser} />;
  }

  const verify = async () => {
    if (!voiceFile && !pdfFile) {
      setError({ message: 'Please upload a voice recording or PDF mandate first.' });
      return;
    }
    setError(null); setBatch(null); setLoading(true); setSelectedIdx(0);
    try {
      const form = new FormData();
      let endpoint;
      if (voiceFile) { form.append('file', voiceFile); endpoint = '/verify/voice'; setLoadingMsg('Transcribing audio…'); }
      else { form.append('file', pdfFile); endpoint = '/verify/pdf'; setLoadingMsg('Extracting text from PDF…'); }
      const isAfricanLang = voiceFile && voiceLang !== 'en';
      if (isAfricanLang) {
        setTimeout(() => loading && setLoadingMsg('Waking the African language model, the first call of a session can take a minute or two…'), 15000);
        setTimeout(() => loading && setLoadingMsg('Still transcribing, thank you for your patience…'), 90000);
      }
      const params = [];
      if (selectedReqId) params.push(`requirement_id=${selectedReqId}`);
      if (voiceFile && voiceLang) params.push(`language=${encodeURIComponent(voiceLang)}`);
      const url = params.length ? `${endpoint}?${params.join('&')}` : endpoint;
      setTimeout(() => loading && setLoadingMsg('Detecting mandates…'), 4000);
      setTimeout(() => loading && setLoadingMsg('Analysing each mandate…'), 8000);
      const resp = await api.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBatch(resp.data); setTab('fields');
    } catch (e) {
      // Trial ended or payment required: open the add-card prompt instead of a generic error.
      if (e.response?.status === 402) {
        setPaymentRequired(true);
        setLoading(false);
        return;
      }
      // Backend may return a structured error object as `detail`, or a plain string
      // (legacy paths). Normalise to {message, action, action_url} shape for the UI.
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        setError(detail);
      } else if (typeof detail === 'string') {
        setError({ message: detail });
      } else if (e.response?.status === 0 || !e.response) {
        setError({
          message: 'Could not reach the server.',
          action: 'Check your internet connection. If the problem persists, contact Eyez support.',
        });
      } else {
        setError({
          message: e.message || 'Verification failed.',
          action: 'Try again. If the problem persists, contact Eyez support.',
        });
      }
    } finally { setLoading(false); setLoadingMsg(''); }
  };

  const reset = () => { setVoiceFile(null); setPdfFile(null); setBatch(null); setError(null); setSelectedIdx(0); };
  const logout = () => {
    auth.logout();
    setUser(null);
    setView('dashboard');
    setBatch(null);
    setVoiceFile(null);
    setPdfFile(null);
    setError(null);
    setSelectedIdx(0);
    setRequirements([]);
    setSelectedReqId('');
  };

  const downloadReport = async (mandateResult) => {
    if (!mandateResult?.report_url) return;
    const filename = mandateResult.report_url.split('/').pop();
    try { await auth.downloadAuthenticated(mandateResult.report_url, filename); }
    catch (err) { alert('Download failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const ackReview = async (vid) => {
    if (!vid) return;
    try {
      await api.post(`/verifications/${vid}/acknowledge-review`);
      setAcknowledgedIds(prev => ({ ...prev, [vid]: true }));
    } catch (err) {
      alert('Could not record the review: ' + (err.response?.data?.detail || err.message));
    }
  };

  const isVerifier = user.role === 'verifier';
  const isCompanyAdmin = user.role === 'company_admin';
  const isSuperAdmin = user.role === 'super_admin';
  const isAdmin = isCompanyAdmin || isSuperAdmin;

  const logoSrc = user.company_logo_url ? `${API}${user.company_logo_url}` : (platform.has_logo ? `${API}/platform-settings/logo` : null);
  const brandName = user.company_name || platform.system_name;
  const brandColour = user.company_primary_colour || platform.primary_colour || '#111827';

  // Currently-selected mandate from the batch
  const result = batch?.mandates?.[selectedIdx];
  const isVoice = result?.source === 'voice';
  const checklistSpec = isVoice ? result?.voice_checklist_spec : result?.written_checklist_spec;
  const checklistData = isVoice ? result?.voice_checklist : result?.written_checklist;
  const fieldsSpec = result?.fields_spec || [];

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FB', fontFamily: 'Inter, sans-serif' }}>
      {paymentRequired && <PaymentRequiredOverlay brandColour={brandColour} onDismiss={() => setPaymentRequired(false)} />}
      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', gap: 10, overflowX: 'auto' }}>
        {logoSrc
          ? <img src={logoSrc} alt={brandName} style={{ height: 28, maxWidth: 110, objectFit: 'contain' }} />
          : <div style={{ width: 28, height: 28, borderRadius: 7, background: brandColour, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>{brandName[0]}</span>
            </div>}
        {user.company_name && <span style={{ fontWeight: 600, fontSize: 15 }}>{user.company_name}</span>}
        <span style={{ color: '#9CA3AF', fontSize: 13 }}>{platform.system_name}</span>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap' }}>
          <button onClick={() => setView('dashboard')} style={navBtn(view === 'dashboard')}>Dashboard</button>
          <button onClick={() => { setView('verify'); reset(); }} style={navBtn(view === 'verify')}>Verify</button>
          <button onClick={() => setView('history')} style={navBtn(view === 'history')}>History</button>
          {isAdmin && <button onClick={() => setView('users')} style={navBtn(view === 'users')}>Users</button>}
          {isAdmin && <button onClick={() => setView('requirements')} style={navBtn(view === 'requirements')}>Rules</button>}
          {isSuperAdmin && <button onClick={() => setView('companies')} style={navBtn(view === 'companies')}>Companies</button>}
          {isAdmin && <button onClick={() => setView('billing')} style={navBtn(view === 'billing')}>Billing</button>}
          {isSuperAdmin && <button onClick={() => setView('payments')} style={navBtn(view === 'payments')}>Payments</button>}
          {isSuperAdmin && <button onClick={() => setView('settings')} style={navBtn(view === 'settings')}>Settings</button>}
          {isSuperAdmin && <button onClick={() => setView('dsr')} style={navBtn(view === 'dsr')}>DSR</button>}
          {isAdmin && <button onClick={() => setView('apikeys')} style={navBtn(view === 'apikeys')}>API Keys</button>}
          <button onClick={() => setView('security')} style={navBtn(view === 'security')}>Security</button>
          <div style={{ width: 1, height: 20, background: '#E5E7EB', margin: '0 4px' }} />
          <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
            {user.email}
            {!isVerifier && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 6px', background: '#EFF6FF', color: '#2563EB', borderRadius: 4, fontWeight: 600 }}>{user.role.replace('_', ' ').toUpperCase()}</span>}
          </span>
          <button onClick={logout} style={{ background: 'transparent', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 12 }}>Sign out</button>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 20px' }}>
        {isCompanyAdmin && user.email_verified === false && <VerifyEmailBanner email={user.email} />}
        {view === 'dashboard' && <Dashboard user={user} />}
        {view === 'history' && <History user={user} />}
        {view === 'companies' && <AdminCompanies />}
        {view === 'users' && <AdminUsers user={user} />}
        {view === 'requirements' && <AdminRequirements user={user} onChange={reloadRequirements} />}
        {view === 'billing' && <AdminBilling user={user} />}
        {view === 'payments' && <AdminPayments />}
        {view === 'settings' && <AdminSettings />}
        {view === 'security' && <AccountSecurity user={user} />}
        {view === 'apikeys' && <AccountAPIKeys user={user} />}
        {view === 'dsr' && <AdminDataSubjectRequests user={user} />}

        {view === 'verify' && !batch && (
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '28px 28px 24px' }}>
            <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 4 }}>Verify a mandate</h1>
            <p style={{ color: '#6B7280', marginBottom: 24, fontSize: 13 }}>Upload a voice recording or signed PDF, checked against your company's requirements. Multi-mandate files are auto-detected and processed individually.</p>

            {requirements.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 6 }}>Ruleset</label>
                <select value={selectedReqId} onChange={e => setSelectedReqId(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', minWidth: 280 }}>
                  <option value="">Default {requirements.find(r => r.is_default) ? `(${requirements.find(r => r.is_default).name})` : ''}</option>
                  {requirements.map(r => <option key={r.id} value={r.id}>{r.name}{r.is_default ? ' (default)' : ''}</option>)}
                </select>
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, color: '#6B7280', display: 'block', marginBottom: 6 }}>Recording language (voice mandates)</label>
              <select value={voiceLang} onChange={e => setVoiceLang(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', minWidth: 280 }}>
                <option value="en">English (South African)</option>
                <option value="af">Afrikaans</option>
                <option value="zu">isiZulu</option>
                <option value="st">Sesotho</option>
              </select>
              {voiceLang !== 'en' && (
                <p style={{ fontSize: 11, color: '#6B7280', marginTop: 6, marginBottom: 0 }}>
                  African language transcription runs through Vulavula (Lelapa AI, South Africa).
                  {voiceLang === 'af'
                    ? ' Your Vulavula key is used if set in Account, API Keys; otherwise OpenAI handles Afrikaans.'
                    : ' Requires a Vulavula API key in Account, API Keys.'}
                </p>
              )}
            </div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
              <UploadZone label="Voice mandate" accept=".wav,.mp3,.m4a,.ogg,.flac" icon="🎙" onFile={f => { setVoiceFile(f); setPdfFile(null); }} loading={loading} activeFile={voiceFile} />
              <UploadZone label="PDF mandate" accept=".pdf" icon="📄" onFile={f => { setPdfFile(f); setVoiceFile(null); }} loading={loading} activeFile={pdfFile} />
            </div>
            {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button onClick={verify} disabled={loading || (!voiceFile && !pdfFile)} style={{ background: loading ? '#9CA3AF' : brandColour, color: '#fff', border: 'none', borderRadius: 9, padding: '11px 28px', fontWeight: 600, fontSize: 14, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
                {loading ? <><Spinner />{loadingMsg || 'Processing…'}</> : 'Verify mandate'}
              </button>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>🔒 Files deleted after processing · Reports retained 7 days · Max 10 mandates per file</span>
            </div>
          </div>
        )}

        {view === 'verify' && batch && (
          <div>
            {/* Batch summary card */}
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 4px 0' }}>
                    {batch.batch_size === 1 ? 'Verification result' : `Batch result: ${batch.batch_size} mandates detected`}
                  </h2>
                  <p style={{ fontSize: 13, color: '#6B7280', margin: 0 }}>
                    {batch.mandates[0]?.filename} · {batch.mandates[0]?.source?.toUpperCase()}
                    {batch.detection?.notes && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>· {batch.detection.notes}</span>}
                  </p>
                </div>
                <button onClick={reset} style={{ background: 'transparent', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 13 }}>New verification</button>
              </div>

              {batch.batch_size > 1 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(140px,1fr))', gap: 10, marginTop: 16 }}>
                  <SummaryStat label="Total" value={batch.summary.total} />
                  <SummaryStat label="Compliant" value={batch.summary.compliant} color="#16A34A" />
                  <SummaryStat label="Non-compliant" value={batch.summary.non_compliant} color="#DC2626" />
                  <SummaryStat label="Incomplete" value={batch.summary.incomplete} color="#D97706" />
                  {batch.summary.error > 0 && <SummaryStat label="Errors" value={batch.summary.error} color="#6B7280" />}
                </div>
              )}
            </div>

            {/* Per-mandate rows */}
            {batch.batch_size > 1 && (
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '12px 8px', marginBottom: 16 }}>
                <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, padding: '6px 14px', margin: 0 }}>Mandates in this batch</p>
                {batch.mandates.map((m, i) => (
                  <div key={i}
                    onClick={() => { setSelectedIdx(i); setTab('fields'); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                      cursor: 'pointer', borderRadius: 10,
                      background: selectedIdx === i ? '#F3F4F6' : 'transparent',
                      border: `1px solid ${selectedIdx === i ? '#D1D5DB' : 'transparent'}`,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#9CA3AF', minWidth: 26 }}>#{i + 1}</span>
                    <ScoreRing score={m.compliance_score || 0} size={42} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: 14, fontWeight: 500, margin: 0, color: '#111827' }}>{m.mandate_identifier || `Mandate ${i + 1}`}</p>
                      <p style={{ fontSize: 12, color: '#6B7280', margin: '2px 0 0 0' }}>
                        {m.fields_present || 0}/{m.fields_total || 0} fields
                        {m.flags?.length > 0 && <span style={{ color: '#DC2626', marginLeft: 8 }}>· {m.flags.length} flag{m.flags.length !== 1 ? 's' : ''}</span>}
                      </p>
                    </div>
                    <Badge verdict={m.overall_verdict} size="sm" />
                    {m.report_url && (
                      <button
                        onClick={e => { e.stopPropagation(); downloadReport(m); }}
                        style={{ background: '#F3F4F6', color: '#111827', border: '1px solid #E5E7EB', borderRadius: 6, padding: '5px 10px', fontSize: 11, fontWeight: 500, cursor: 'pointer' }}
                      >↓ PDF</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Detail view of selected mandate */}
            {result && (
              <>
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                  <ScoreRing score={result.compliance_score || 0} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                      <Badge verdict={result.overall_verdict} />
                      {batch.batch_size > 1 && <span style={{ fontSize: 12, color: '#2563EB', background: '#EFF6FF', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>Mandate {selectedIdx + 1} of {batch.batch_size}</span>}
                      {result.mandate_identifier && <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{result.mandate_identifier}</span>}
                      <span style={{ fontSize: 12, color: '#9CA3AF' }}>{result.fields_present}/{result.fields_total} fields · {result.source?.toUpperCase()}</span>
                      {result.requirement_used && <span style={{ fontSize: 11, color: '#2563EB', background: '#EFF6FF', padding: '2px 8px', borderRadius: 4 }}>{result.requirement_used}</span>}
                    </div>
                    <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{result.summary}</p>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {result.report_url && <button onClick={() => downloadReport(result)} style={{ background: '#F3F4F6', color: '#111827', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 16px', fontWeight: 500, fontSize: 13, cursor: 'pointer' }}>↓ PDF report</button>}
                  </div>
                </div>

                {result.requires_review && (
                  acknowledgedIds[result.verification_id] ? (
                    <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 12, padding: '12px 16px', marginBottom: 16 }}>
                      <span style={{ fontSize: 13, color: '#15803D', fontWeight: 500 }}>Marked as reviewed. This sign-off is recorded for the audit trail.</span>
                    </div>
                  ) : (
                    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '14px 16px', marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 220 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#92400E', marginBottom: 3 }}>Needs human review before this result is relied on</div>
                        <div style={{ fontSize: 12.5, color: '#92400E', lineHeight: 1.5 }}>This is an AI-assisted check and can be wrong.{result.review_reason ? ' Reason: ' + result.review_reason + '.' : ''} Confirm against the document before using this result.</div>
                      </div>
                      <button onClick={() => ackReview(result.verification_id)} style={{ background: '#92400E', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap' }}>I have reviewed this result</button>
                    </div>
                  )
                )}

                {isVoice && <VoiceConfidenceNotice />}

                <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                  {['fields', 'checklist', 'flags', 'transcript'].map(t => (
                    <button key={t} onClick={() => setTab(t)} style={{ background: tab === t ? '#fff' : 'transparent', border: tab === t ? '1px solid #E5E7EB' : '1px solid transparent', borderRadius: 8, padding: '6px 16px', cursor: 'pointer', fontWeight: tab === t ? 500 : 400, color: tab === t ? '#111827' : '#6B7280', fontSize: 13 }}>
                      {t.charAt(0).toUpperCase() + t.slice(1)}
                      {t === 'flags' && result.flags?.length > 0 && <span style={{ marginLeft: 6, background: '#FEE2E2', color: '#DC2626', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>{result.flags.length}</span>}
                    </button>
                  ))}
                </div>

                {tab === 'fields' && (
                  <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 12 }}>
                      {fieldsSpec.map(s => <FieldCard key={s.key} label={s.label} fkey={s.key} data={result.fields?.[s.key]} />)}
                    </div>
                  </div>
                )}

                {tab === 'checklist' && (
                  <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
                    <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 12px 0' }}>{isVoice ? 'Voice mandate validation checklist' : 'Written mandate validation checklist'}</p>
                    {(checklistSpec || []).map(s => <CheckRow key={s.key} label={s.label} data={checklistData?.[s.key]} />)}
                    <div style={{ marginTop: 14, display: 'flex', gap: 16, fontSize: 12, color: '#6B7280' }}>
                      <span>✓ {Object.values(checklistData || {}).filter(v => v?.present).length} passed</span>
                      <span>✗ {Object.values(checklistData || {}).filter(v => !v?.present).length} failed</span>
                    </div>
                  </div>
                )}

                {tab === 'flags' && (
                  <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
                    {!result.flags?.length ? <p style={{ color: '#16A34A', fontWeight: 500 }}>No flags. Mandate looks clean.</p>
                      : result.flags.map((f, i) => <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: '0.5px solid #F3F4F6' }}>
                          <span style={{ color: '#DC2626', fontWeight: 600 }}>!</span>
                          <p style={{ fontSize: 13, color: '#374151', margin: 0 }}>{f}</p>
                        </div>)}
                  </div>
                )}

                {tab === 'transcript' && (
                  <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' }}>
                    <p style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 10 }}>Raw {isVoice ? 'transcription' : 'extracted text'}{batch.batch_size > 1 ? ` for mandate ${selectedIdx + 1}` : ''}</p>
                    <pre style={{ background: '#F8F9FB', borderRadius: 8, padding: '14px 16px', fontSize: 12, lineHeight: 1.7, color: '#374151', fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 400, overflowY: 'auto', border: '1px solid #E5E7EB', margin: 0 }}>{result.transcript || '-'}</pre>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function VerifyEmail() {
  const [state, setState] = useState('working');
  const [msg, setMsg] = useState('');
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    if (!token) { setState('error'); setMsg('This link is missing its token.'); return; }
    api.post('/signup/verify-email', { token })
      .then(() => setState('ok'))
      .catch((e) => { setState('error'); setMsg(e.response?.data?.detail || 'This link is invalid or has expired.'); });
  }, []);
  const box = { minHeight: '100vh', background: '#F8F9FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif', padding: 20 };
  const card = { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: '32px 28px', maxWidth: 420, textAlign: 'center' };
  const linkBtn = { display: 'inline-block', background: '#111827', color: '#fff', textDecoration: 'none', padding: '11px 22px', borderRadius: 9, fontWeight: 600, fontSize: 14 };
  return (
    <div style={box}>
      <div style={card}>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 16 }}>eyez</div>
        {state === 'working' && <p style={{ color: '#6B7280' }}>Confirming your email…</p>}
        {state === 'ok' && (
          <>
            <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>Email confirmed</h1>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20 }}>Thanks. Your email address is verified.</p>
            <a href="/login" style={linkBtn}>Go to sign in</a>
          </>
        )}
        {state === 'error' && (
          <>
            <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>Could not confirm</h1>
            <p style={{ color: '#6B7280', fontSize: 14, marginBottom: 20 }}>{msg}</p>
            <a href="/login" style={linkBtn}>Go to sign in</a>
          </>
        )}
      </div>
    </div>
  );
}

function VerifyEmailBanner({ email }) {
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const resend = () => {
    setBusy(true);
    api.post('/signup/resend-verification', {})
      .then((r) => setSent(r.data?.sent !== false))
      .catch(() => setSent(false))
      .finally(() => setBusy(false));
  };
  return (
    <div style={{ background: '#FEFCE8', border: '1px solid #FDE68A', borderRadius: 10, padding: '12px 16px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 14, color: '#92400E' }}>
        Please confirm your email ({email}) to secure your account.{sent ? ' A new link is on its way.' : ''}
      </span>
      {!sent && (
        <button onClick={resend} disabled={busy} style={{ marginLeft: 'auto', background: '#111827', color: '#fff', border: 'none', borderRadius: 7, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: busy ? 'wait' : 'pointer' }}>
          {busy ? 'Sending…' : 'Resend link'}
        </button>
      )}
    </div>
  );
}

function PaymentRequiredOverlay({ brandColour, onDismiss }) {
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvc: '' });
  const [bill, setBill] = useState({ address: '', city: '', postcode: '', country: 'South Africa' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [extCode, setExtCode] = useState('');
  const [extBusy, setExtBusy] = useState(false);
  const [extErr, setExtErr] = useState(null);
  const [extDone, setExtDone] = useState(null);

  const redeemCode = async () => {
    setExtErr(null);
    if (!extCode.trim()) { setExtErr('Enter your code.'); return; }
    setExtBusy(true);
    try {
      const r = await api.post('/billing/redeem-code', { code: extCode.trim() });
      setExtDone(r.data);
      setTimeout(() => window.location.reload(), 1800);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setExtErr(typeof detail === 'string' ? detail : (detail?.message || e.message || 'Could not redeem the code.'));
      setExtBusy(false);
    }
  };

  const fmtCard = v => v.replace(/\D/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ');
  const fmtExp = v => { const d = v.replace(/\D/g, '').slice(0, 4); return d.length < 3 ? d : d.slice(0, 2) + ' / ' + d.slice(2); };

  const pay = async () => {
    setErr(null);
    const digits = card.number.replace(/\s/g, '');
    if (digits.length < 13) return setErr('Enter a valid card number.');
    if (!card.name.trim()) return setErr('Cardholder name is required.');
    if (!/^\d{2}\s\/\s\d{2}$/.test(card.expiry)) return setErr('Expiry must be MM / YY.');
    if (card.cvc.length < 3) return setErr('CVC must be at least 3 digits.');
    if (!bill.address.trim() || !bill.city.trim() || !bill.postcode.trim()) return setErr('Billing address, city and postcode are required.');
    const [mm, yy] = card.expiry.split(' / ');
    setBusy(true);
    try {
      const r = await api.post('/signup/payment', {
        pan: digits, cardholder_name: card.name, expiry_mm: mm, expiry_yy: yy, cvc: card.cvc,
        billing_address: bill.address, billing_city: bill.city, billing_postcode: bill.postcode, billing_country: bill.country,
      });
      if (!r.data?.success) { setErr(r.data?.error_message || 'The card was declined. Try another card.'); setBusy(false); return; }
      window.location.reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || 'Payment could not be processed.');
      setBusy(false);
    }
  };

  const inp = { width: '100%', padding: '10px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, marginBottom: 10, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '28px 26px', width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 4px' }}>Your trial has ended</h2>
        <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 18px', lineHeight: 1.5 }}>Add a payment method to continue. Your card is charged for the first month now and monthly after that. Card details go straight to Peach Payments and are never stored by Eyez.</p>
        <input placeholder="Card number" value={card.number} onChange={e => setCard({ ...card, number: fmtCard(e.target.value) })} style={inp} inputMode="numeric" />
        <input placeholder="Cardholder name" value={card.name} onChange={e => setCard({ ...card, name: e.target.value })} style={inp} />
        <div style={{ display: 'flex', gap: 10 }}>
          <input placeholder="MM / YY" value={card.expiry} onChange={e => setCard({ ...card, expiry: fmtExp(e.target.value) })} style={{ ...inp, flex: 1 }} inputMode="numeric" />
          <input placeholder="CVC" value={card.cvc} onChange={e => setCard({ ...card, cvc: e.target.value.replace(/\D/g, '').slice(0, 4) })} style={{ ...inp, flex: 1 }} inputMode="numeric" />
        </div>
        <input placeholder="Billing address" value={bill.address} onChange={e => setBill({ ...bill, address: e.target.value })} style={inp} />
        <div style={{ display: 'flex', gap: 10 }}>
          <input placeholder="City" value={bill.city} onChange={e => setBill({ ...bill, city: e.target.value })} style={{ ...inp, flex: 1 }} />
          <input placeholder="Postcode" value={bill.postcode} onChange={e => setBill({ ...bill, postcode: e.target.value })} style={{ ...inp, flex: 1 }} />
        </div>
        {err && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', color: '#DC2626', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <button onClick={pay} disabled={busy} style={{ width: '100%', background: busy ? '#9CA3AF' : (brandColour || '#111827'), color: '#fff', border: 'none', borderRadius: 9, padding: '12px', fontWeight: 600, fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer' }}>{busy ? 'Processing…' : 'Pay and continue'}</button>

        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 12, fontWeight: 600, color: '#374151', margin: '0 0 6px' }}>Have an extension code?</p>
          {extDone ? (
            <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '10px 12px', color: '#166534', fontSize: 13 }}>
              Code accepted. Your access is extended, no charge before {new Date(extDone.free_until).toLocaleDateString()}. Reloading…
            </div>
          ) : (
            <>
              <p style={{ fontSize: 11, color: '#6B7280', margin: '0 0 8px', lineHeight: 1.5 }}>
                If Eyez gave you an extension code, redeem it here to keep using the platform without a card payment.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="EYEZ-XXXX-XXXX-XXXX" value={extCode}
                  onChange={e => setExtCode(e.target.value.toUpperCase())}
                  style={{ ...inp, flex: 1, marginBottom: 0, fontFamily: 'monospace', textTransform: 'uppercase' }}
                  spellCheck={false} autoComplete="off" />
                <button onClick={redeemCode} disabled={extBusy}
                  style={{ background: '#fff', color: '#111827', border: '1px solid #D1D5DB', borderRadius: 8, padding: '10px 16px', fontWeight: 600, fontSize: 13, cursor: extBusy ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
                  {extBusy ? 'Redeeming…' : 'Redeem'}
                </button>
              </div>
              {extErr && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '8px 12px', color: '#DC2626', fontSize: 12, marginTop: 8 }}>{extErr}</div>}
            </>
          )}
        </div>

        <button onClick={onDismiss} disabled={busy} style={{ width: '100%', background: 'transparent', color: '#6B7280', border: 'none', padding: '10px', fontSize: 13, cursor: 'pointer', marginTop: 6 }}>Not now</button>
      </div>
    </div>
  );
}

function VoiceConfidenceNotice() {
  return (
    <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 14, marginTop: 1 }}>🎙</span>
      <div style={{ fontSize: 12.5, color: '#075985', lineHeight: 1.55 }}>
        <strong>Voice transcription notice.</strong> Automated transcription can misread spoken names, abbreviated statement names, and strings of digits such as ID, account, and branch numbers. The confidence labels show the model's own certainty, not a guarantee of correctness. Confirm the numeric fields against the client's records before acting on this mandate.
      </div>
    </div>
  );
}

function SummaryStat({ label, value, color }) {
  return <div style={{ background: color ? `${color}10` : '#F8F9FB', border: `1px solid ${color ? color + '40' : '#E5E7EB'}`, borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
    <p style={{ fontSize: 10, color: '#6B7280', textTransform: 'uppercase', margin: 0, marginBottom: 4, letterSpacing: 0.5 }}>{label}</p>
    <p style={{ fontSize: 22, fontWeight: 700, margin: 0, color: color || '#111827' }}>{value}</p>
  </div>;
}

const navBtn = (active) => ({
  background: active ? '#F3F4F6' : 'transparent', border: 'none',
  color: active ? '#111827' : '#6B7280', borderRadius: 6,
  padding: '6px 10px', cursor: 'pointer', fontSize: 13,
  fontWeight: active ? 500 : 400, whiteSpace: 'nowrap',
});
